import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import {
  EnvelopeSimple,
  LinkSimple,
  MagnifyingGlass,
  Phone,
  UserCircle,
  WarningCircle,
} from '@phosphor-icons/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import api from '../../services/api'
import { WhatsappBadge, formatWaId, isPlaceholderGuestEmail } from '../../components/WhatsappBadge'

/**
 * Painel do VISITANTE (chat da tela de login, deslogado).
 *
 * O chat nasce com um usuário-sombra; o que o visitante informou e o que a
 * triagem automática concluiu vêm de `guestSession` / `guestTriage` no
 * GET /chats/:id. Enquanto não houver vínculo, a conta candidata é só uma
 * pista (o e-mail bate) — a identidade NÃO está confirmada.
 */

export const GUEST_DIAGNOSIS_LABELS: Record<string, string> = {
  ACTIVE: 'Conta ativa (link de senha enviado)',
  ACTIVE_NEED_CHANGE_PW: 'Ativa, nunca trocou a senha temporária',
  RECENT_PURCHASE: 'Compra recente (senha do boas-vindas)',
  MISSING_PAYMENT: 'Pendência de pagamento',
  INACTIVE: 'Conta inativa',
  PENDING: 'Cadastro pendente',
  DELETED: 'Conta excluída',
  NOT_FOUND: 'E-mail sem conta',
}

export const GUEST_REASON_LABELS: Record<string, string> = {
  login: 'Senha não funciona',
  missing_payment: 'Falta de pagamento',
  inactive: 'Conta inativa',
  '2fa': 'Código de verificação',
  other: 'Outro',
}

/** Botões/etapas da triagem que o monólito grava em events[]. */
const EVENT_LABELS: Record<string, string> = {
  received: 'Recebeu o e-mail',
  got_email: 'Recebeu o e-mail',
  not_received: 'Não recebeu o e-mail',
  no_email: 'Não recebeu o e-mail',
  alt_email: 'Informou e-mail alternativo',
  altEmail: 'Informou e-mail alternativo',
  resend: 'Pediu reenvio',
  reset_sent: 'Link de redefinição enviado',
  talk_to_agent: 'Pediu para falar com atendente',
  agent: 'Pediu para falar com atendente',
  handoff: 'Encaminhado para atendente',
  triage: 'Triagem automática',
}

const DIAGNOSIS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  ACTIVE_NEED_CHANGE_PW: 'yellow',
  RECENT_PURCHASE: 'blue',
  MISSING_PAYMENT: 'orange',
  INACTIVE: 'red',
  PENDING: 'yellow',
  DELETED: 'red',
  NOT_FOUND: 'gray',
}

interface TriageEvent {
  type?: string
  action?: string
  event?: string
  kind?: string
  label?: string
  at?: string
  createdAt?: string
  timestamp?: string
  ts?: string
  email?: string
  value?: string
}

interface TriageDetails {
  email?: string
  name?: string
  phone?: string
  reason?: string
  candidateUserId?: number | null
  accountStatus?: string | null
  diagnosisCode?: string | null
  agentSummary?: string | null
  resetSent?: boolean
  altEmail?: string | null
  resends?: number
  events?: TriageEvent[]
}

interface GuestChat {
  id: number
  userId: number
  assignedAgentId: number | null
  isGuest?: boolean
  guestEmail?: string | null
  guestName?: string | null
  channel?: 'web' | 'whatsapp'
  whatsappWaId?: string | null
  whatsappProfileName?: string | null
  guestPhone?: string | null
  guestReason?: string | null
  candidateUserId?: number | null
  accountStatus?: string | null
  diagnosisCode?: string | null
  guestLinkedUserId?: number | null
  guestSession?: {
    diagnosisJson?: TriageDetails | Record<string, unknown> | null
    triageLog?: { events?: TriageEvent[] } | TriageEvent[] | null
    linkedUserId?: number | null
    linkedAt?: string | null
    createdAt?: string
  } | null
  guestTriage?: { createdAt?: string; details?: TriageDetails | null } | null
}

interface FoundUser {
  id: number
  name: string
  email: string
  status: string
}

const eventCode = (ev: TriageEvent) => ev.type || ev.action || ev.event || ev.kind || ''
const eventTime = (ev: TriageEvent) => ev.at || ev.createdAt || ev.timestamp || ev.ts || null

const formatTime = (value?: string | null) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return format(d, "dd/MM HH:mm", { locale: ptBR })
}

export function GuestBadge({ size = 'sm' }: { size?: 'xs' | 'sm' }) {
  return (
    <Badge
      bg="purple.500"
      color="white"
      fontSize={size === 'xs' ? { base: '9px', md: '10px' } : { base: '2xs', md: 'xs' }}
      borderRadius="full"
      px={size === 'xs' ? 1 : 1.5}
      noOfLines={1}
      title="Visitante da tela de login (não estava logado)"
    >
      Visitante
    </Badge>
  )
}

export default function GuestInfoPanel({
  chatId,
  compact = false,
  onLinked,
  renderUserPanel,
}: {
  chatId: number
  /** Só o resumo da triagem (depois do vínculo). */
  compact?: boolean
  /** Chamado quando o vínculo é feito aqui OU já veio feito da API. */
  onLinked?: (userId: number) => void
  /** Painel da conta candidata (dados, tags, pedidos...) — injetado para evitar import circular. */
  renderUserPanel?: (userId: number) => ReactNode
}) {
  const toast = useToast()
  const [chat, setChat] = useState<GuestChat | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLinking, setIsLinking] = useState(false)
  /** userId aguardando confirmação inline (sem window.confirm). */
  const [confirmingUserId, setConfirmingUserId] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<FoundUser[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notifiedLinkRef = useRef<number | null>(null)

  const bgSubtle = useColorModeValue('purple.50', 'whiteAlpha.100')
  const bgCard = useColorModeValue('gray.50', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  const fetchChat = useCallback(async () => {
    try {
      const res = await api.get(`/chats/${chatId}`)
      setChat(res.data)
    } catch (error) {
      console.error('Error fetching guest chat:', error)
    } finally {
      setIsLoading(false)
    }
  }, [chatId])

  useEffect(() => {
    setIsLoading(true)
    setChat(null)
    setConfirmingUserId(null)
    setQuery('')
    setResults([])
    notifiedLinkRef.current = null
    fetchChat()
  }, [fetchChat])

  // Vínculo já feito (por outro atendente, ou antes de abrir): avisa o pai uma vez.
  useEffect(() => {
    const linked = chat?.guestLinkedUserId
    if (linked && notifiedLinkRef.current !== linked) {
      notifiedLinkRef.current = linked
      onLinked?.(Number(linked))
    }
  }, [chat?.guestLinkedUserId, onLinked])

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      return
    }
    searchTimer.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const res = await api.get('/users/search', { params: { q } })
        setResults(res.data.data || [])
      } catch (error) {
        console.error('Error searching users:', error)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 400)
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [query])

  const handleLink = async (userId: number) => {
    setIsLinking(true)
    try {
      const res = await api.post(`/chats/${chatId}/guest/link`, { userId })
      toast({ title: 'Visitante vinculado à conta', status: 'success', duration: 3000 })
      setConfirmingUserId(null)
      setQuery('')
      setResults([])
      const updated = res.data?.chat
      if (updated) setChat(updated)
      else await fetchChat()
    } catch (error: any) {
      toast({
        title: 'Não foi possível vincular',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsLinking(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Spinner size="md" color="purple.400" />
      </Box>
    )
  }

  if (!chat) {
    return (
      <Box p={4}>
        <Text color={textMuted} fontSize="sm">Informações do visitante não disponíveis</Text>
      </Box>
    )
  }

  const triageDetails: TriageDetails =
    chat.guestTriage?.details ||
    ((chat.guestSession?.diagnosisJson as TriageDetails | null) ?? {})
  const rawEmail = chat.guestEmail || triageDetails.email || ''
  const isWhatsappGuest = isPlaceholderGuestEmail(rawEmail) || chat.channel === 'whatsapp'
  const email = isPlaceholderGuestEmail(rawEmail) ? '' : rawEmail
  const name = chat.guestName || chat.whatsappProfileName || triageDetails.name || ''
  const phone = chat.guestPhone || triageDetails.phone || ''
  const reason = chat.guestReason || triageDetails.reason || ''
  const diagnosisCode = chat.diagnosisCode || triageDetails.diagnosisCode || ''
  const agentSummary =
    triageDetails.agentSummary ||
    ((chat.guestSession?.diagnosisJson as TriageDetails | null)?.agentSummary ?? '')
  const candidateUserId = chat.candidateUserId || triageDetails.candidateUserId || null
  const linkedUserId = chat.guestLinkedUserId || null

  const rawLog = chat.guestSession?.triageLog
  const logEvents: TriageEvent[] = Array.isArray(rawLog)
    ? rawLog
    : rawLog && Array.isArray(rawLog.events)
      ? rawLog.events
      : []
  const events: TriageEvent[] =
    triageDetails.events && triageDetails.events.length > 0 ? triageDetails.events : logEvents

  // Função de render (não componente interno): evita remontar a cada render.
  const renderConfirmLink = (userId: number, label: string) =>
    confirmingUserId === userId ? (
      <HStack spacing={2} w="100%">
        <Text fontSize="xs" color={textSecondary} flex={1}>
          Confirmar vínculo com #{userId}? As mensagens do visitante passam a ser desta conta.
        </Text>
        <Button size="xs" colorScheme="purple" isLoading={isLinking} onClick={() => handleLink(userId)}>
          Confirmar
        </Button>
        <Button size="xs" variant="ghost" isDisabled={isLinking} onClick={() => setConfirmingUserId(null)}>
          Cancelar
        </Button>
      </HStack>
    ) : (
      <Button
        size="xs"
        colorScheme="purple"
        variant="solid"
        leftIcon={<LinkSimple size={14} />}
        isDisabled={isLinking}
        onClick={() => setConfirmingUserId(userId)}
      >
        {label}
      </Button>
    )

  return (
    <VStack align="stretch" spacing={compact ? 2 : 4} p={compact ? 3 : { base: 3, md: 4 }}>
      {/* Quem é */}
      <Box bg={bgSubtle} borderRadius="lg" p={3} border="1px solid" borderColor={borderColor}>
        <HStack spacing={2} mb={2} align="center">
          {isWhatsappGuest ? <WhatsappBadge /> : <GuestBadge />}
          <Text fontSize="xs" color={textMuted}>
            {isWhatsappGuest
              ? 'Visitante via WhatsApp'
              : compact ? 'triagem da tela de login' : 'não estava logado ao abrir o chat'}
          </Text>
        </HStack>
        <VStack align="stretch" spacing={1}>
          {(!isWhatsappGuest || email) && (
            <HStack spacing={1} align="center">
              <Box as="span" color={textMuted} display="inline-flex" flexShrink={0} lineHeight={1}>
                <EnvelopeSimple size={14} />
              </Box>
              <Text fontSize="sm" color={textPrimary} fontWeight="medium" wordBreak="break-all">
                {email || '-'}
              </Text>
            </HStack>
          )}
          {isWhatsappGuest && chat.whatsappWaId && (
            <HStack spacing={1} align="center">
              <Box as="span" color={textMuted} display="inline-flex" flexShrink={0} lineHeight={1}>
                <Phone size={14} />
              </Box>
              <Text fontSize="sm" color={textPrimary} fontWeight="medium">
                {formatWaId(chat.whatsappWaId)}
              </Text>
            </HStack>
          )}
          <HStack spacing={1} align="center">
            <Box as="span" color={textMuted} display="inline-flex" flexShrink={0} lineHeight={1}>
              <UserCircle size={14} />
            </Box>
            <Text fontSize="sm" color={textSecondary}>{name || 'Nome não informado'}</Text>
          </HStack>
          <HStack spacing={1} align="center">
            <Box as="span" color={textMuted} display="inline-flex" flexShrink={0} lineHeight={1}>
              <Phone size={14} />
            </Box>
            <Text fontSize="sm" color={textSecondary}>{phone || 'WhatsApp não informado'}</Text>
          </HStack>
        </VStack>
      </Box>

      {/* Motivo + diagnóstico */}
      <Box>
        <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={1} textTransform="uppercase">
          Motivo informado
        </Text>
        <Text fontSize="sm" color={textPrimary}>
          {GUEST_REASON_LABELS[reason] || reason || '-'}
        </Text>
      </Box>

      <Box>
        <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={1} textTransform="uppercase">
          Diagnóstico da triagem
        </Text>
        <HStack spacing={2} flexWrap="wrap" mb={agentSummary ? 1 : 0}>
          <Badge colorScheme={DIAGNOSIS_COLOR[diagnosisCode] || 'gray'} borderRadius="full" px={2} textTransform="none">
            {GUEST_DIAGNOSIS_LABELS[diagnosisCode] || diagnosisCode || 'Sem diagnóstico'}
          </Badge>
          {chat.accountStatus && (
            <Text fontSize="2xs" color={textMuted}>status na triagem: {chat.accountStatus}</Text>
          )}
        </HStack>
        {agentSummary && (
          <Text fontSize="sm" color={textSecondary} whiteSpace="pre-wrap">
            {agentSummary}
          </Text>
        )}
        {triageDetails.resetSent && (
          <Text fontSize="xs" color="green.400" mt={1}>
            Link de redefinição de senha enviado na triagem
            {triageDetails.resends ? ` (${triageDetails.resends} reenvio${triageDetails.resends > 1 ? 's' : ''})` : ''}
          </Text>
        )}
        {triageDetails.altEmail && (
          <Text fontSize="xs" color={textSecondary} mt={1}>
            E-mail alternativo informado: <b>{triageDetails.altEmail}</b>
          </Text>
        )}
      </Box>

      {/* Linha do tempo curta */}
      {events.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={1} textTransform="uppercase">
            Passos na triagem
          </Text>
          <VStack align="stretch" spacing={1} pl={2} borderLeft="2px solid" borderColor="purple.400">
            {events.map((ev, idx) => {
              const code = eventCode(ev)
              const when = formatTime(eventTime(ev))
              const extra = ev.email || ev.value
              return (
                <HStack key={idx} spacing={2} align="start">
                  <Text fontSize="2xs" color={textMuted} minW="70px" pt="2px">
                    {when || '—'}
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    {ev.label || EVENT_LABELS[code] || code || 'evento'}
                    {extra ? ` · ${extra}` : ''}
                  </Text>
                </HStack>
              )
            })}
          </VStack>
        </Box>
      )}

      {compact && linkedUserId && (
        <Text fontSize="xs" color={textMuted}>
          Vinculado à conta #{linkedUserId}
          {chat.guestSession?.linkedAt ? ` em ${formatTime(chat.guestSession.linkedAt)}` : ''}
        </Text>
      )}

      {!compact && !linkedUserId && (
        <>
          <Divider borderColor={borderColor} />

          {/* Conta candidata */}
          {candidateUserId ? (
            <Box border="1px dashed" borderColor="purple.400" borderRadius="lg" overflow="hidden">
              <Box p={3} bg={bgCard}>
                <HStack spacing={2} mb={2} align="center">
                  <Box as="span" color="purple.400" display="inline-flex" lineHeight={1}>
                    <WarningCircle size={16} weight="fill" />
                  </Box>
                  <Text fontSize="sm" fontWeight="bold" color={textPrimary}>
                    Conta candidata (não confirmada)
                  </Text>
                </HStack>
                <Alert status="warning" borderRadius="md" fontSize="xs" py={2} mb={2}>
                  <AlertIcon boxSize={4} />
                  O e-mail informado bate com o usuário #{candidateUserId}, mas a identidade NÃO foi
                  confirmada. Confira antes de passar qualquer dado da conta.
                </Alert>
                {renderConfirmLink(Number(candidateUserId), 'Vincular a esta conta')}
              </Box>
              {renderUserPanel && <Box>{renderUserPanel(Number(candidateUserId))}</Box>}
            </Box>
          ) : (
            <Alert status="info" borderRadius="md" fontSize="xs" py={2}>
              <AlertIcon boxSize={4} />
              Nenhuma conta com este e-mail. Se o visitante tiver cadastro com outro e-mail, vincule abaixo.
            </Alert>
          )}

          {/* Vincular a outro usuário */}
          <Box>
            <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={1} textTransform="uppercase">
              Vincular a outro usuário
            </Text>
            <HStack spacing={2}>
              <Box as="span" color={textMuted} display="inline-flex" flexShrink={0} lineHeight={1}>
                <MagnifyingGlass size={14} />
              </Box>
              <Input
                size="sm"
                placeholder="id, nome ou e-mail"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {isSearching && <Spinner size="xs" />}
            </HStack>
            {results.length > 0 && (
              <VStack align="stretch" spacing={1} mt={2}>
                {results.map((u) => (
                  <Box key={u.id} p={2} bg={bgCard} borderRadius="md" border="1px solid" borderColor={borderColor}>
                    <HStack justify="space-between" align="start" spacing={2}>
                      <VStack align="start" spacing={0} minW={0} flex={1}>
                        <Text fontSize="sm" color={textPrimary} noOfLines={1}>
                          #{u.id} · {u.name}
                        </Text>
                        <Text fontSize="xs" color={textMuted} noOfLines={1} wordBreak="break-all">
                          {u.email}
                        </Text>
                        {u.status && u.status !== 'active' && (
                          <Badge colorScheme="orange" fontSize="2xs" textTransform="none">
                            status: {u.status}
                          </Badge>
                        )}
                      </VStack>
                      {confirmingUserId !== u.id && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="purple"
                          isDisabled={isLinking}
                          onClick={() => setConfirmingUserId(u.id)}
                        >
                          Vincular
                        </Button>
                      )}
                    </HStack>
                    {confirmingUserId === u.id && (
                      <Box mt={2}>
                        {renderConfirmLink(u.id, 'Vincular')}
                      </Box>
                    )}
                  </Box>
                ))}
              </VStack>
            )}
            {query.trim().length >= 2 && !isSearching && results.length === 0 && (
              <Text fontSize="xs" color={textMuted} mt={1}>Nenhum usuário encontrado</Text>
            )}
          </Box>
        </>
      )}
    </VStack>
  )
}
